import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, Plus, Trash2, Loader2 } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { useReviewPanel } from '@/contexts/ReviewPanelContext'
import { apiFetch } from '@/lib/apiFetch'
import { toast } from 'sonner'

type Row = { abbr: string; full: string; user_edited?: boolean }

const inputClass =
  'h-7 w-full rounded-md border border-border bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring'

export function AbbreviationsPanel() {
  const {
    abbreviations,
    onAbbreviationsChange,
    draftId,
    setViewMode,
  } = useReviewPanel()

  // Local editable copy. Sync from props when the audit refreshes.
  const [rows, setRows] = useState<Row[]>(abbreviations)
  const lastSyncedRef = useRef(abbreviations)
  useEffect(() => {
    if (abbreviations !== lastSyncedRef.current) {
      setRows(abbreviations)
      lastSyncedRef.current = abbreviations
    }
  }, [abbreviations])

  const [isSaving, setIsSaving] = useState(false)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => a.abbr.localeCompare(b.abbr)),
    [rows],
  )

  const persist = (next: Row[]) => {
    if (!draftId) return
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(async () => {
      setIsSaving(true)
      try {
        const res = await apiFetch(`/api/drafts/${draftId}/abbreviations`, {
          method: 'PATCH',
          body: JSON.stringify({
            abbreviations: next
              .filter((r) => r.abbr.trim())
              .map((r) => ({
                abbr: r.abbr.trim(),
                full: r.full.trim(),
                user_edited: true,
              })),
          }),
        })
        if (!res.ok) throw new Error(await res.text())
        const data = await res.json()
        onAbbreviationsChange(data.abbreviations ?? [])
      } catch (err) {
        toast.error('Could not save abbreviations.')
        console.error(err)
      } finally {
        setIsSaving(false)
      }
    }, 400)
  }

  const updateRow = (i: number, patch: Partial<Row>) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch, user_edited: true } : r))
    setRows(next)
    persist(next)
  }

  const removeRow = (i: number) => {
    const next = rows.filter((_, idx) => idx !== i)
    setRows(next)
    persist(next)
  }

  const addRow = () => {
    setRows((prev) => [...prev, { abbr: '', full: '', user_edited: true }])
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <button
          type="button"
          onClick={() => setViewMode('triage')}
          className="text-muted-foreground hover:text-foreground"
          title="Back to review"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
        </button>
        <div className="flex flex-col">
          <span className="text-sm font-medium">Abbreviations</span>
          <span className="text-xs text-muted-foreground">
            Edits stick across re-audits.
          </span>
        </div>
        {isSaving && (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground ml-auto" strokeWidth={1.5} />
        )}
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <div className="p-3 space-y-1.5">
          {sortedRows.length === 0 && (
            <p className="text-xs text-muted-foreground px-1">
              No abbreviations yet. Run an audit, or add one manually.
            </p>
          )}
          {sortedRows.map((row, i) => {
            const realIndex = rows.indexOf(row)
            const isAi = !row.user_edited
            return (
              <div
                key={`${row.abbr}-${i}`}
                className="flex items-center gap-1.5 rounded-md border border-border/60 bg-card p-1.5"
              >
                <input
                  type="text"
                  value={row.abbr}
                  onChange={(e) => updateRow(realIndex, { abbr: e.target.value })}
                  placeholder="ABBR"
                  className={inputClass + ' font-medium flex-[1]'}
                />
                <input
                  type="text"
                  value={row.full}
                  onChange={(e) => updateRow(realIndex, { full: e.target.value })}
                  placeholder="full expansion"
                  className={inputClass + ' flex-[2]'}
                />
                <span
                  className={
                    'text-[10px] px-1.5 py-0.5 rounded shrink-0 ' +
                    (isAi
                      ? 'bg-muted text-muted-foreground'
                      : 'bg-[#E1F5EE] text-[#0F6E56]')
                  }
                  title={isAi ? 'Generated by AI' : 'Edited by you'}
                >
                  {isAi ? 'AI' : 'You'}
                </span>
                <button
                  type="button"
                  onClick={() => removeRow(realIndex)}
                  className="text-muted-foreground hover:text-destructive p-1 shrink-0"
                  title="Remove"
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
              </div>
            )
          })}
        </div>
      </ScrollArea>

      <div className="border-t border-border p-3">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={addRow}
          disabled={!draftId}
        >
          <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
          Add abbreviation
        </Button>
      </div>
    </div>
  )
}
